import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import useAxiosPrivate from "../utils/useAxiosPrivate";
import { parseUtc } from "../utils/utcDate";

function BoolChip({
  value,
  trueLabel = "YES",
  falseLabel = "NO",
  trueColor = "default",
  falseColor = "default",
}) {
  return (
    <Chip
      label={value ? trueLabel : falseLabel}
      color={value ? trueColor : falseColor}
      size="small"
      variant={value ? "filled" : "outlined"}
      sx={{ fontWeight: 600 }}
    />
  );
}

function FaultChip({ value }) {
  return (
    <Chip
      label={value ? "FAULT" : "OK"}
      color={value ? "error" : "success"}
      size="small"
      sx={{ fontWeight: 600 }}
    />
  );
}

function Num({ value, unit, fault = false, warn = false }) {
  return (
    <Typography
      variant="body2"
      fontWeight={fault || warn ? 700 : 600}
      color={fault ? "error.main" : warn ? "warning.main" : undefined}
    >
      {value}
      {unit ? ` ${unit}` : ""}
    </Typography>
  );
}

// A band with no label at one end is open at that end, which is how a floor-only limit
// like high-side pressure is expressed.
function bandFault(value, band) {
  if (value == null || !band) return null;
  if (band.belowLabel && value < band.min) return band.belowLabel;
  if (band.aboveLabel && value > band.max) return band.aboveLabel;
  return null;
}

function tankLevelState(value, step) {
  if (value == null || !step) return null;
  if (step.faultBelow != null && value < step.faultBelow) {
    return { color: "error", label: "Alert" };
  }
  if (step.warnBelow != null && value < step.warnBelow) {
    return { color: "warning", label: "Warning" };
  }
  return null;
}

function TankLevelValue({ value, step }) {
  const state = tankLevelState(value, step);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Num
        value={value}
        unit="%"
        fault={state?.color === "error"}
        warn={state?.color === "warning"}
      />
      {state && (
        <Chip label={state.label} color={state.color} size="small" sx={{ fontWeight: 600 }} />
      )}
    </Stack>
  );
}

function BandValue({ value, unit, band }) {
  const fault = bandFault(value, band);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Num value={value} unit={unit} fault={Boolean(fault)} />
      {fault && <Chip label={fault} color="error" size="small" sx={{ fontWeight: 600 }} />}
    </Stack>
  );
}

function Row({ label, value }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      sx={{ py: 0.75 }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box>{value}</Box>
    </Stack>
  );
}

function Section({ title, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography
        variant="overline"
        sx={{ display: "block", mb: 1, letterSpacing: 1 }}
      >
        {title}
      </Typography>
      <Stack divider={<Divider flexItem />}>{children}</Stack>
    </Paper>
  );
}

function SubGroup({ title, children }) {
  return (
    <Box sx={{ py: 0.75 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.25, fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Box sx={{ pl: 1 }}>{children}</Box>
    </Box>
  );
}

function SystemInfoSection({ data }) {
  return (
    <Section title="System">
      <Row label="Name" value={<Num value={data.system} />} />
      <Row
        label="Type"
        value={
          <Chip
            label={data.systemType}
            color="secondary"
            size="small"
            sx={{ fontWeight: 700 }}
          />
        }
      />
    </Section>
  );
}

function SuppressantSection({ data }) {
  return (
    <Section title="Suppressant & Pressure">
      <Row
        label="Tank level"
        value={
          <TankLevelValue value={data.tankLevel} step={data.thresholds?.tankLevel} />
        }
      />
      <Row
        label="High-side pressure"
        value={
          <BandValue
            value={data.highSidePressure}
            unit="psi"
            band={data.thresholds?.highSidePressure}
          />
        }
      />
      <Row
        label="Low-side pressure"
        value={
          <BandValue
            value={data.lowSidePressure}
            unit="psi"
            band={data.thresholds?.lowSidePressure}
          />
        }
      />
    </Section>
  );
}

function ClimateSection({ data }) {
  const c = data.climate ?? {};
  return (
    <Section title="Climate">
      <Row
        label="Temperature"
        value={<BandValue value={c.temperature} unit="°F" band={data.thresholds?.temperature} />}
      />
      <Row
        label="High-temp alarm (PLC)"
        value={
          <BoolChip
            value={c.highTempAlarm}
            trueLabel="ACTIVE"
            falseLabel="INACTIVE"
            trueColor="warning"
          />
        }
      />
      <Row
        label="Low-temp alarm (PLC)"
        value={
          <BoolChip
            value={c.lowTempAlarm}
            trueLabel="ACTIVE"
            falseLabel="INACTIVE"
            trueColor="warning"
          />
        }
      />
    </Section>
  );
}

function PowerSuppliesSection({ data }) {
  return (
    <Section title="Power supplies">
      <Row
        label="AC power"
        value={
          <BoolChip
            value={!data.powerFail}
            trueLabel="OK"
            falseLabel="LOST"
            trueColor="success"
            falseColor="error"
          />
        }
      />
      <Row
        label="Power supply 1"
        value={
          <BoolChip
            value={data.powerSupply1_OK}
            trueLabel="OK"
            falseLabel="FAULT"
            trueColor="success"
            falseColor="error"
          />
        }
      />
      <Row
        label="Power supply 2"
        value={
          <BoolChip
            value={data.powerSupply2_OK}
            trueLabel="OK"
            falseLabel="FAULT"
            trueColor="success"
            falseColor="error"
          />
        }
      />
    </Section>
  );
}

function UpsSection({ title, ups }) {
  return (
    <Section title={title}>
      <Row label="Input voltage" value={<Num value={ups.inputVoltage} unit="V" />} />
      <Row label="Input current" value={<Num value={ups.inputCurrent} unit="A" />} />
      <Row label="Output voltage" value={<Num value={ups.outputVoltage} unit="V" />} />
      <Row label="Output current" value={<Num value={ups.outputCurrent} unit="A" />} />
      <Row label="Battery voltage" value={<Num value={ups.batteryVoltage} unit="V" />} />
      <Row
        label="Battery charge current"
        value={<Num value={ups.batteryChargeCurrent} unit="A" />}
      />
      <Row
        label="Battery temperature"
        value={<Num value={ups.batteryTemperature} unit="°F" />}
      />
      <Row
        label="Main power"
        value={
          <BoolChip
            value={ups.mainPowerOK}
            trueLabel="OK"
            falseLabel="LOST"
            trueColor="success"
            falseColor="error"
          />
        }
      />
      <Row
        label="On battery"
        value={
          <BoolChip
            value={ups.batteryPowerActive}
            trueLabel="ACTIVE"
            falseLabel="STANDBY"
            trueColor="warning"
          />
        }
      />
      <Row
        label="Output"
        value={
          <BoolChip
            value={ups.outputActive}
            trueLabel="ACTIVE"
            falseLabel="INACTIVE"
            trueColor="success"
          />
        }
      />
    </Section>
  );
}

// Temporarily hidden until Primary Network telemetry is flowing to the DB. Flip
// to true to restore the "Primary installed" (primaryInstalled) and "Station
// connection" (stationConnection) rows.
const SHOW_PRIMARY_NETWORK = false;

function NetworkSection({ data }) {
  const n = data.network ?? {};
  return (
    <Section title="Network">
      {SHOW_PRIMARY_NETWORK && (
        <Row
          label="Primary installed"
          value={<BoolChip value={n.primaryInstalled} />}
        />
      )}
      <Row
        label="Failover active"
        value={
          <BoolChip
            value={n.failoverActive}
            trueLabel="ACTIVE"
            falseLabel="STANDBY"
            trueColor="warning"
          />
        }
      />
      {SHOW_PRIMARY_NETWORK && (
        <Row
          label="Station connection"
          value={
            <BoolChip
              value={n.stationConnection}
              trueLabel="ONLINE"
              falseLabel="OFFLINE"
              trueColor="success"
              falseColor="error"
            />
          }
        />
      )}
    </Section>
  );
}

function ReleasingPanelSection({ data }) {
  const rps = data.releasingPanelSignals ?? {};
  const isBsu = data.systemType === "BSU";
  return (
    <Section title={isBsu ? "Supervisory" : "Releasing panel & supervisory"}>
      <Row label="Alarm" value={<FaultChip value={rps.alarm} />} />
      <Row label="Supervisory" value={<FaultChip value={rps.supervisory} />} />
      <Row label="Trouble" value={<FaultChip value={rps.trouble} />} />
      {!isBsu && (
        <Row label="Water flow" value={<FaultChip value={rps.waterFlow} />} />
      )}
      <Row
        label="Supervisory alert"
        value={<FaultChip value={data.supervisoryAlert} />}
      />
    </Section>
  );
}

function ConfigurableIoSection({ data }) {
  const inputs = Object.entries(data.configurableIO?.inputs ?? {});
  const outputs = Object.entries(data.configurableIO?.outputs ?? {});
  return (
    <Section title="Configurable I/O">
      {inputs.length > 0 && (
        <SubGroup title="Inputs">
          <Stack divider={<Divider flexItem />}>
            {inputs.map(([id, io]) => (
              <Row
                key={`in-${id}`}
                label={`#${id} ${io.label}`}
                value={
                  <BoolChip
                    value={io.active}
                    trueLabel="ACTIVE"
                    falseLabel="INACTIVE"
                    trueColor="warning"
                  />
                }
              />
            ))}
          </Stack>
        </SubGroup>
      )}
      {outputs.length > 0 && (
        <SubGroup title="Outputs">
          <Stack divider={<Divider flexItem />}>
            {outputs.map(([id, io]) => (
              <Row
                key={`out-${id}`}
                label={`#${id} ${io.label}`}
                value={
                  <BoolChip
                    value={io.active}
                    trueLabel="ACTIVE"
                    falseLabel="INACTIVE"
                    trueColor="warning"
                  />
                }
              />
            ))}
          </Stack>
        </SubGroup>
      )}
    </Section>
  );
}

function MainEButtonSection({ data }) {
  const e = data.mainE_Button ?? {};
  return (
    <Section title="Main emergency button">
      <Row
        label="Voltage"
        value={<BandValue value={e.voltage} unit="V" band={data.thresholds?.eButtonVoltage} />}
      />
      <Row label="Disconnected" value={<FaultChip value={e.disconnected} />} />
      <Row label="Shorted" value={<FaultChip value={e.shorted} />} />
    </Section>
  );
}

function ArrangementSection({ index, arrangement, isEd, eButtonBand }) {
  return (
    <Section title={`Arrangement ${index}`}>
      <Row
        label="Detection only"
        value={<BoolChip value={arrangement.detectionOnly} />}
      />
      {!isEd && arrangement.monitor?.used && (
        <SubGroup title="Monitor">
          <Row
            label="Connected"
            value={
              <BoolChip
                value={arrangement.monitor.connected}
                trueColor="success"
                falseColor="error"
                trueLabel="YES"
                falseLabel="NO"
              />
            }
          />
          <Row label="Voltage" value={<Num value={arrangement.monitor.voltage} unit="V" />} />
          <Row label="Serial" value={<Num value={arrangement.monitor.serialNumber} />} />
        </SubGroup>
      )}
      {!isEd && arrangement.valve?.used && (
        <SubGroup title="Valve">
          <Row
            label="Connected"
            value={
              <BoolChip
                value={arrangement.valve.connected}
                trueColor="success"
                falseColor="error"
                trueLabel="YES"
                falseLabel="NO"
              />
            }
          />
          <Row label="Serial" value={<Num value={arrangement.valve.serialNumber} />} />
        </SubGroup>
      )}
      {(arrangement.leftThermal?.used || arrangement.rightThermal?.used) && (
        <SubGroup title="Thermal detectors">
          {arrangement.leftThermal?.used && (
            <Row
              label="Left"
              value={
                <BoolChip
                  value={arrangement.leftThermal.connected}
                  trueLabel="CONNECTED"
                  falseLabel="OFFLINE"
                  trueColor="success"
                  falseColor="error"
                />
              }
            />
          )}
          {arrangement.rightThermal?.used && (
            <Row
              label="Right"
              value={
                <BoolChip
                  value={arrangement.rightThermal.connected}
                  trueLabel="CONNECTED"
                  falseLabel="OFFLINE"
                  trueColor="success"
                  falseColor="error"
                />
              }
            />
          )}
        </SubGroup>
      )}
      {arrangement.leftOFD?.used && (
        <OfdSubGroup title="Flame detector (left OFD)" ofd={arrangement.leftOFD} />
      )}
      {arrangement.rightOFD?.used && (
        <OfdSubGroup title="Flame detector (right OFD)" ofd={arrangement.rightOFD} />
      )}
      {arrangement.e_Button?.used && (
        <SubGroup title="Emergency button">
          <Row
            label="Voltage"
            value={
              <BandValue value={arrangement.e_Button.voltage} unit="V" band={eButtonBand} />
            }
          />
          <Row
            label="Disconnected"
            value={<FaultChip value={arrangement.e_Button.disconnected} />}
          />
          <Row label="Shorted" value={<FaultChip value={arrangement.e_Button.shorted} />} />
        </SubGroup>
      )}
    </Section>
  );
}

function OfdSubGroup({ title, ofd }) {
  return (
    <SubGroup title={title}>
      <Row
        label="Connected"
        value={
          <BoolChip
            value={ofd.connected}
            trueLabel="CONNECTED"
            falseLabel="OFFLINE"
            trueColor="success"
            falseColor="error"
          />
        }
      />
      <Row label="Fault active" value={<FaultChip value={ofd.faultActive} />} />
      <Row label="Analog signal" value={<Num value={ofd.analogSignal} unit="mA" />} />
      {ofd.signalStatus && (
        <Row
          label="Signal status"
          value={
            <Chip
              label={ofd.signalStatus.toUpperCase()}
              color={ofd.signalStatus === "Normal" ? "success" : "error"}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          }
        />
      )}
    </SubGroup>
  );
}

// ── Empty-detection helpers ───────────────────────────────────────────────────

function suppressantHasContent(data) {
  return (
    (data.tankLevel ?? 0) !== 0 ||
    (data.highSidePressure ?? 0) !== 0 ||
    (data.lowSidePressure ?? 0) !== 0
  );
}

function climateHasContent(data) {
  const c = data.climate;
  if (!c) return false;
  return (
    (c.temperature ?? 0) !== 0 ||
    Boolean(c.highTempAlarm) ||
    Boolean(c.lowTempAlarm)
  );
}

function ioHasContent(data) {
  const inputs = Object.values(data.configurableIO?.inputs ?? {});
  const outputs = Object.values(data.configurableIO?.outputs ?? {});
  return [...inputs, ...outputs].some(
    (io) => io.active || (io.label && io.label !== "string"),
  );
}

function buildSections(data) {
  const isEd = data.systemType === "ED";
  const hasTankAndClimate = !(isEd || data.systemType === "CF");
  const sections = [];
  sections.push({ id: "system", node: <SystemInfoSection data={data} /> });
  if (hasTankAndClimate && suppressantHasContent(data)) {
    sections.push({ id: "suppressant", node: <SuppressantSection data={data} /> });
  }
  if (hasTankAndClimate && climateHasContent(data)) {
    sections.push({ id: "climate", node: <ClimateSection data={data} /> });
  }
  sections.push({ id: "power", node: <PowerSuppliesSection data={data} /> });
  if (data.upS1?.connected) {
    sections.push({ id: "ups1", node: <UpsSection title="UPS 1" ups={data.upS1} /> });
  }
  if (data.upS2?.connected) {
    sections.push({ id: "ups2", node: <UpsSection title="UPS 2" ups={data.upS2} /> });
  }
  sections.push({ id: "network", node: <NetworkSection data={data} /> });
  sections.push({ id: "releasing", node: <ReleasingPanelSection data={data} /> });
  if (ioHasContent(data)) {
    sections.push({ id: "io", node: <ConfigurableIoSection data={data} /> });
  }
  if (data.mainE_Button?.used) {
    sections.push({ id: "mainE", node: <MainEButtonSection data={data} /> });
  }
  for (let i = 1; i <= 4; i++) {
    const a = data[`arrangement${i}`];
    if (a?.used) {
      sections.push({
        id: `arrangement${i}`,
        node: (
          <ArrangementSection
            index={i}
            arrangement={a}
            isEd={isEd}
            eButtonBand={data.thresholds?.eButtonVoltage}
          />
        ),
      });
    }
  }
  return sections;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function SystemDiagnostics({ productName }) {
  const axiosPrivate = useAxiosPrivate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!productName) return undefined;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    axiosPrivate
      .get(
        `/api/systemhealth/getHealthData/${encodeURIComponent(productName)}`,
        { signal: controller.signal },
      )
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        setError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [productName, retryKey, axiosPrivate]);

  if (loading) {
    return (
      <Stack spacing={2}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={120} animation="wave" />
        ))}
      </Stack>
    );
  }

  if (error) {
    const status = error.response?.status;
    const detail = status
      ? `Server responded with ${status} ${error.response.statusText || ""}`.trim()
      : error.message || "Network error";
    return (
      <Alert
        severity="error"
        variant="outlined"
        sx={{
          "& .MuiAlert-action": { alignItems: "center", pt: 0, mt: 0 },
        }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => setRetryKey((k) => k + 1)}
          >
            RETRY
          </Button>
        }
      >
        <AlertTitle sx={{ fontWeight: 700 }}>
          Unable To Load Diagnostics
        </AlertTitle>
        {detail}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No diagnostic readings available for this system.
        </Typography>
      </Box>
    );
  }

  const sections = buildSections(data);

  if (sections.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No diagnostic readings available for this system.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {sections.map((s) => (
        <Box key={s.id}>{s.node}</Box>
      ))}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pt: 1 }}
      >
        <Typography variant="caption" color="text.secondary">
          Reading from{" "}
          {parseUtc(data.readingTimestamp)?.toLocaleString(undefined, {
            timeZoneName: "short",
          }) ?? "—"}
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshRoundedIcon />}
          onClick={() => setRetryKey((k) => k + 1)}
        >
          Refresh
        </Button>
      </Stack>
    </Stack>
  );
}
